import { useEffect, useState } from 'react';

import { validateCustomModel } from '@/api/registry';
import { buildPreviewPayload } from '../payload';

const EMPTY = { byLine: {}, specErrors: [] };

/**
 * Debounced `/validate/` call behind the balance editor's inline annotations.
 *
 * Re-fires ~300 ms after the last change to anything that affects the balance
 * check. Returns `{ byLine, specErrors }`; `byLine` is keyed by line number so
 * the editor can look up each row's annotation directly.
 */
export const useModuleValidation = ({ inputs, outputs, params, balance }) => {
  const [annotations, setAnnotations] = useState(EMPTY);

  useEffect(() => {
    const payload = buildPreviewPayload({ inputs, outputs, params, balance });

    const ready = !!balance && payload.data.inputs.length > 0 && payload.data.outputs.length > 0;

    // Set by the cleanup below when a newer edit supersedes this request, so
    // a response that lands out of order can't overwrite fresher annotations.
    let superseded = false;

    const handle = setTimeout(async () => {
      if (!ready) {
        setAnnotations(EMPTY);
        return;
      }
      try {
        const res = await validateCustomModel(payload);
        if (superseded) return;
        const byLine = {};
        for (const ann of res?.data?.annotations ?? []) {
          byLine[ann.line] = ann;
        }
        setAnnotations({ byLine, specErrors: res?.data?.spec_errors ?? [] });
      } catch {
        // Network or 4xx — leave annotations stale. Don't spam errors.
      }
    }, 300);

    return () => {
      superseded = true;
      clearTimeout(handle);
    };
  }, [inputs, outputs, params, balance]);

  return annotations;
};
